import axios, { AxiosError } from "axios";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "react-query";
import {
  AuditEntry,
  ConfigureSiteResponse,
  Region,
  RosterConfig,
  SiteConfigurationDetail,
} from "../model";
import {
  ConfigureRosterRequest,
  ConfigureSiteRequest,
  CreateRegionRequest,
  CreateSiteRequest,
  MoveRosterRequest,
  MoveSiteRequest,
} from "../request";
import { HttpClientError } from "../../../../providers/HttpClient";

const CONFIGURATION_TREE_KEY = "SiteConfigurationTree";
const SITE_CONFIGURATION_KEY = "SiteConfiguration";
const SITE_AUDIT_LOG_KEY = "SiteConfigurationAuditLog";
const AUDIT_LOG_LIMIT = 50;

interface ConfigurationTreeResponse {
  regions: Region[];
}

const throwHttpClientError = (error: unknown): never => {
  const axiosError = error as AxiosError;
  const customError = new Error(HttpClientError(axiosError));
  customError.cause = axiosError;
  throw customError;
};

// ── Configuration tree (Regions → Sites → Rosters) ──────────────

const SitesAndRegionsTreeApiService = async (): Promise<Region[]> => {
  try {
    const { data } = await axios.get<ConfigurationTreeResponse>(
      "/shift-site-groups/configuration-tree/"
    );
    return data.regions;
  } catch (error) {
    return throwHttpClientError(error);
  }
};

const useSitesAndRegionsTreeApiService = (): UseQueryResult<
  Region[],
  Error | AxiosError
> =>
  useQuery<Region[], Error | AxiosError>(
    [CONFIGURATION_TREE_KEY],
    SitesAndRegionsTreeApiService,
    { keepPreviousData: true }
  );

// ── Per-Site configuration (Site defaults + Rosters) ────────────

const SiteConfigurationApiService = async (
  siteId: string
): Promise<SiteConfigurationDetail> => {
  try {
    const { data } = await axios.get<SiteConfigurationDetail>(
      `/shift-sites/${siteId}/configuration/`
    );
    return data;
  } catch (error) {
    return throwHttpClientError(error);
  }
};

const useSiteConfigurationApiService = (
  siteId: string
): UseQueryResult<SiteConfigurationDetail, Error | AxiosError> =>
  useQuery<SiteConfigurationDetail, Error | AxiosError>(
    [SITE_CONFIGURATION_KEY, siteId],
    () => SiteConfigurationApiService(siteId),
    { enabled: Boolean(siteId), keepPreviousData: true }
  );

// ── Audit log (gated — only fetched when the dialog is open) ─────

const SiteAuditLogApiService = async (
  siteId: string
): Promise<AuditEntry[]> => {
  try {
    const { data } = await axios.get<AuditEntry[]>(
      `/shift-sites/${siteId}/audit-log/`,
      { params: { limit: AUDIT_LOG_LIMIT } }
    );
    return data;
  } catch (error) {
    return throwHttpClientError(error);
  }
};

const useSiteAuditLogApiService = (
  siteId: string,
  options: { enabled: boolean }
): UseQueryResult<AuditEntry[], Error | AxiosError> =>
  useQuery<AuditEntry[], Error | AxiosError>(
    [SITE_AUDIT_LOG_KEY, siteId, AUDIT_LOG_LIMIT],
    () => SiteAuditLogApiService(siteId),
    { enabled: options.enabled && Boolean(siteId), keepPreviousData: true }
  );

// ── Mutations ───────────────────────────────────────────────────

interface ConfigureSiteVariables {
  siteId: string;
  data: ConfigureSiteRequest;
}

const useConfigureSiteMutation = (): UseMutationResult<
  ConfigureSiteResponse,
  Error | AxiosError,
  ConfigureSiteVariables
> => {
  const queryClient = useQueryClient();
  return useMutation<
    ConfigureSiteResponse,
    Error | AxiosError,
    ConfigureSiteVariables
  >(
    async ({ siteId, data }) => {
      try {
        const { data: response } = await axios.patch<ConfigureSiteResponse>(
          `/shift-sites/${siteId}/configure/`,
          data
        );
        return response;
      } catch (error) {
        return throwHttpClientError(error);
      }
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([
          SITE_CONFIGURATION_KEY,
          variables.siteId,
        ]);
        queryClient.invalidateQueries([SITE_AUDIT_LOG_KEY, variables.siteId]);
      },
    }
  );
};

interface ConfigureRosterVariables {
  rosterId: string;
  siteId: string;
  data: ConfigureRosterRequest;
}

const useConfigureRosterMutation = (): UseMutationResult<
  RosterConfig,
  Error | AxiosError,
  ConfigureRosterVariables
> => {
  const queryClient = useQueryClient();
  return useMutation<
    RosterConfig,
    Error | AxiosError,
    ConfigureRosterVariables
  >(
    async ({ rosterId, data }) => {
      try {
        const { data: response } = await axios.patch<RosterConfig>(
          `/shift-locations/${rosterId}/configure/`,
          data
        );
        return response;
      } catch (error) {
        return throwHttpClientError(error);
      }
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([
          SITE_CONFIGURATION_KEY,
          variables.siteId,
        ]);
        queryClient.invalidateQueries([SITE_AUDIT_LOG_KEY, variables.siteId]);
      },
    }
  );
};

interface MoveRosterVariables {
  rosterId: string;
  siteId: string;
  data: MoveRosterRequest;
}

const useMoveRosterMutation = (): UseMutationResult<
  RosterConfig,
  Error | AxiosError,
  MoveRosterVariables
> => {
  const queryClient = useQueryClient();
  return useMutation<RosterConfig, Error | AxiosError, MoveRosterVariables>(
    async ({ rosterId, data }) => {
      try {
        const { data: response } = await axios.post<RosterConfig>(
          `/shift-locations/${rosterId}/move/`,
          data
        );
        return response;
      } catch (error) {
        return throwHttpClientError(error);
      }
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([
          SITE_CONFIGURATION_KEY,
          variables.siteId,
        ]);
        queryClient.invalidateQueries([CONFIGURATION_TREE_KEY]);
      },
    }
  );
};

const useCreateRegionMutation = (): UseMutationResult<
  Region,
  Error | AxiosError,
  CreateRegionRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<Region, Error | AxiosError, CreateRegionRequest>(
    async (payload) => {
      try {
        const { data } = await axios.post<Region>(
          "/shift-site-groups/",
          payload
        );
        return data;
      } catch (error) {
        return throwHttpClientError(error);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CONFIGURATION_TREE_KEY]);
      },
    }
  );
};

const useCreateSiteMutation = (): UseMutationResult<
  { id: string; name: string },
  Error | AxiosError,
  CreateSiteRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<
    { id: string; name: string },
    Error | AxiosError,
    CreateSiteRequest
  >(
    async (payload) => {
      try {
        const { data } = await axios.post<{ id: string; name: string }>(
          "/shift-sites/",
          payload
        );
        return data;
      } catch (error) {
        return throwHttpClientError(error);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CONFIGURATION_TREE_KEY]);
      },
    }
  );
};

interface MoveSiteVariables extends MoveSiteRequest {
  /** Path param — the Site being moved. Not part of the PATCH body. */
  siteId: string;
}

const useMoveSiteMutation = (): UseMutationResult<
  { id: string; name: string },
  Error | AxiosError,
  MoveSiteVariables
> => {
  const queryClient = useQueryClient();
  return useMutation<
    { id: string; name: string },
    Error | AxiosError,
    MoveSiteVariables
  >(
    async ({ siteId, ...payload }) => {
      try {
        const { data } = await axios.patch<{ id: string; name: string }>(
          `/shift-sites/${siteId}/`,
          payload
        );
        return data;
      } catch (error) {
        return throwHttpClientError(error);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CONFIGURATION_TREE_KEY]);
      },
    }
  );
};

export {
  useSitesAndRegionsTreeApiService,
  useSiteConfigurationApiService,
  useSiteAuditLogApiService,
  useConfigureSiteMutation,
  useConfigureRosterMutation,
  useMoveRosterMutation,
  useCreateRegionMutation,
  useCreateSiteMutation,
  useMoveSiteMutation,
};
